import moment from 'moment'

export default class ApiEstablishmentManager {
    /**
     * Get default scheduling configuration
     * @returns {Promise<Object>} The default scheduling configuration
     */
    static async getDefaultScheduling(establishmentId) {
        try {
            const response = await axios.get(`/back-office/v2/establishment/scheduling/${establishmentId}/get-default`)
            return response.data
        } catch (error) {
            console.error('Error fetching default scheduling:', error)
            throw error
            console.error('Error fetching default scheduling:', error)
            throw error
        }
    }

    /**
     * Set default scheduling configuration
     * @param {Array} schedulingData - The scheduling data to save
     * @returns {Promise<Object>} The response from the server
     */
    static async setDefaultScheduling(establishmentId, schedulingData) {
        try {
            const response = await axios.post(`/back-office/v2/establishment/scheduling/${establishmentId}/set-default`, {
                scheduling: schedulingData,
            })
            return response.data
        } catch (error) {
            console.error('Error saving default scheduling:', error)
            throw error
            console.error('Error saving default scheduling:', error)
            throw error
        }
    }

    /**
     * Get custom scheduling configuration
     * @returns {Promise<Object>} The custom scheduling
     */

    static async getCustomScheduling(establishmentId) {
        try {
            const response = await axios.get(`/back-office/v2/establishment/scheduling/${establishmentId}/custom-schedules`)
            return response.data
        } catch (error) {
            console.error('Error fetching custom scheduling:', error)
            throw error
        }
    }

    static async setCustomScheduling(establishmentId, payload) {
        try {
            const response = await axios.post(
                `/back-office/v2/establishment/scheduling/${establishmentId}/custom-schedules`,
                payload
            )
            return response.data
        } catch (error) {
            console.error('Error saving custom scheduling:', error)
            throw error
        }
    }

    /**
     * Get scheduling configuration for a specific period
     * @param {Object} params
     * @param {number} [params.establishmentId] - Optional establishment ID
     * @param {string} params.strat - Start date in YYYY-MM-DD format
     * @param {string} params.end - End date in YYYY-MM-DD format
     * @returns {Promise<Object>} The scheduling configuration for the period
     */
    static async getScheduling({ start, end, establishmentId }) {
        try {
            const response = await axios.get(`/back-office/v2/establishment/scheduling/${establishmentId}/get-per-period`, {
                params: {
                    start,
                    end,
                },
            })
            return response.data
        } catch (error) {
            console.error('Error fetching scheduling for period:', error)
            throw error
        }
    }
    /**
     * Set scheduling configuration
     * @param {number} establishmentId - ID of the establishment
     * @param {Object} payload - Scheduling data (including start & end if required)
     * @returns {Promise<Object>} - API response
     */
    static async setScheduling(establishmentId, payload) {
        try {
            const response = await axios.post(
                `/back-office/v2/establishment/scheduling/${establishmentId}/set-per-period`,
                payload
            )
            return response.data
        } catch (error) {
            console.error('Error setting scheduling:', error)
            throw error
        }
    }

    /**
     * Delete scheduling configuration for a specific period
     * @param {number} establishmentId - ID of the establishment
     * @param {Object} payload - Scheduling data (including start & end dates)
     * @returns {Promise<Object>} - API response
     */
    static async deleteScheduling(establishmentId, payload) {
        try {
            const response = await axios.delete(`/back-office/v2/establishment/scheduling/${establishmentId}/delete`, {
                params: payload,
            })
            return response.data
        } catch (error) {
            console.error('Error deleting scheduling:', error)
            throw error
        }
    }
    /**
     * Update scheduling configuration for a specific day
     * @param {Object} params
     * @param {number} params.establishmentId - Establishment ID
     * @param {Object} params.schedule - The full schedule object including date, day, isOpen, periods, lunches, etc.
     * @returns {Promise<Object>} Server response
     */
    static async updateSchedulingDay({ establishmentId, schedule }) {
        try {
            const response = await axios.post(`/back-office/v2/establishment/scheduling/${establishmentId}/update-day`, {
                ...schedule,
            })
            return response.data
        } catch (error) {
            console.error('Error updating scheduling for the day:', error)
            throw error
        }
    }

    /**
     * Get holiday scheduling configuration for a specific period
     * @param {Object} params
     * @param {number} [params.establishmentId] - Optional establishment ID
     * @param {string} params.start - Start date in YYYY-MM-DD format
     * @param {string} params.end - End date in YYYY-MM-DD format
     * @returns {Promise<Object>} The scheduling configuration for the period
     */
    static async getHolidayScheduling({ start, end, establishmentId }) {
        try {
            // Adjust start to beginning of the week (Monday) and end to end of the week (Sunday)
            const startOfWeek = moment(start).startOf('isoWeek').format('YYYY-MM-DD')
            const endOfWeek = moment(end).endOf('isoWeek').format('YYYY-MM-DD')

            const response = await axios.get(`/back-office/v2/establishment/scheduling/${establishmentId}/get-per-period`, {
                params: {
                    start: startOfWeek,
                    end: endOfWeek,
                    type: 'holiday',
                },
            })
            return response.data
        } catch (error) {
            console.error('Error fetching scheduling for period:', error)
            throw error
        }
    }

    static formatDaysForApi(data, label) {
        console.log('formatDaysForApi', data)
        return data.map((day) => ({
            day: day.day,
            is_open: day.enabled ? true : false,
            start: day.periods && day.periods[0] ? day.periods[0].start : '00:00',
            end: day.periods && day.periods[0] ? day.periods[0].end : '00:00',
            lunch_start: day.lunches && day.lunches[0] ? day.lunches[0].start : null,
            lunch_end: day.lunches && day.lunches[0] ? day.lunches[0].end : null,
            label: label,
        }))
    }

    // Helper function
    static getMondayOfISOWeek(week, year) {
        const simple = new Date(year, 0, 1 + (week - 1) * 7)
        const dow = simple.getDay()
        const ISOweekStart = new Date(simple)

        if (dow <= 4) {
            ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1)
        } else {
            ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay())
        }

        return ISOweekStart
    }

    /**
     * Get exception schedules for a specific period
     * @param {Object} params
     * @param {string} params.start - Start date in YYYY-MM-DD format
     * @param {string} params.end - End date in YYYY-MM-DD format
     * @param {number} params.establishmentId - ID of the establishment
     * @returns {Promise<Array>} Array of exception schedules
     */
    static async getExceptionSchedules({ start, end, establishmentId }) {
        try {
            const response = await axios.get(`/back-office/v2/establishment/scheduling/${establishmentId}/exceptions`, {
                params: {
                    start,
                    end,
                },
            })
            return response.data
        } catch (error) {
            console.error('Error fetching exception schedules:', error)
            throw error
        }
    }

    /**
     * Delete an exception schedule
     * @param {number} establishmentId - ID of the establishment
     * @param {number} exceptionId - ID of the exception to delete
     * @returns {Promise<Object>} Response from the server
     */
    static async deleteExceptionSchedule(establishmentId, exceptionId) {
        try {
            const response = await axios.delete(
                `/back-office/v2/establishment/scheduling/${establishmentId}/exceptions/${exceptionId}`
            )
            return response.data
        } catch (error) {
            console.error('Error deleting exception schedule:', error)
            throw error
        }
    }

    /**
     * Update an exception schedule
     * @param {number} establishmentId - ID of the establishment
     * @param {number} exceptionId - ID of the exception to update
     * @param {Object} exceptionData - Updated exception data with all necessary fields
     * @returns {Promise<Object>} Updated exception data
     */
    static async updateExceptionSchedule(establishmentId, exceptionId, exceptionData) {
        try {
            const response = await axios.put(
                `/back-office/v2/establishment/scheduling/${establishmentId}/exceptions/${exceptionId}`,
                exceptionData
            )
            return response.data
        } catch (error) {
            console.error('Error updating exception schedule:', error)
            throw error
        }
    }

    /**
     * Create a new exception schedule
     * @param {number} establishmentId - ID of the establishment
     * @param {Object} exceptionData - New exception data with all necessary fields
     * @param {string} exceptionData.start - Start datetime (YYYY-MM-DD HH:MM:SS)
     * @param {string} exceptionData.end - End datetime (YYYY-MM-DD HH:MM:SS)
     * @param {number} exceptionData.status - Status (1 for closed, other for custom exception)
     * @returns {Promise<Object>} Created exception data
     */
    static async createExceptionSchedule(establishmentId, exceptionData) {
        try {
            const response = await axios.post(
                `/back-office/v2/establishment/scheduling/${establishmentId}/exceptions`,
                exceptionData
            )
            return response.data
        } catch (error) {
            console.error('Error creating exception schedule:', error)
            throw error
        }
    }

    static setExceptionStructure(establishmentId, exceptionData) {
        return {
            establishment_id: establishmentId,
            start: exceptionData.start + (exceptionData.startTime ? ' ' + exceptionData.startTime : ''),
            end: exceptionData.end + (exceptionData.endTime ? ' ' + exceptionData.endTime : ''),
            status: exceptionData.reason.id,
            label: exceptionData.title,
            color: exceptionData.color,
        }
    }

    static formatDateYMD(date) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0') // mois 0-11
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }
}
